import {platformSetTimeout} from '@platform';

import ComponentDisplayName from '../Bindings/React/Components/Constants/ComponentDisplayName';
import * as Errors from '../Shared/Constants/Errors';

import FocusableNode from './FocusableNode';
import ILookupTable from './Interfaces/ILookupTable';
import IParentLookupTable from './Interfaces/IParentLookupTable';
import JustifiedFocusWorker from './JustifiedFocusWorker';

/**
 * A static class containing various utility methods responsible for building up and manipulating
 * the tree and lookup table of focusable nodes.
 */

class TreeWorker {
    /**
     * A map of node IDs against timer IDs for pending reordering operations. Ensures that multiple
     * updates can be made in parallel.
     */

    private static pendingReorderNodeIdMap: Record<string, ReturnType<typeof platformSetTimeout>> = {};

    /**
     * Adds a focusable node to the tree and lookup table.
     */

    public static registerFocusable(
        lookupTable: ILookupTable,
        parentLookupTable: IParentLookupTable,
        node: FocusableNode
    ): FocusableNode | void {
        const existingNode = lookupTable[node.id];

        if (existingNode && existingNode !== node) {
            throw new Error(Errors.NODE_NOT_UNIQUE(node.id));
        } else if (existingNode) {
            // If node already registered, abort

            return;
        }

        const ownChildren = parentLookupTable[node.id];

        if (ownChildren && ownChildren.length > 0) {
            // Assign pending children to their parent

            TreeWorker.collectPendingChildren(parentLookupTable, node);
        }

        // Register node in lookup table

        lookupTable[node.id] = node;

        const {parentId} = node;

        if (parentId === '') {
            // Node has no parent and is therefore the root of the tree, return it

            return node;
        }

        const parent = lookupTable[parentId];

        if (!parent) {
            // Parent does not yet exist, insert into `parentLookupTable` to be consumed when parent
            // mounts

            TreeWorker.insertNodeIntoParentLookupTable(parentLookupTable, parentId, node);

            return;
        }

        // Parent already exists, node has mounted dynamically or lazily after the initial render

        node.parent = parent;

        // Push the new node into the parent's `children` array.

        // NB: By default, all dynamically rendered focusable nodes that are mounted *after* their
        // parent container, will be added to the *end* of the container's `children` array. This
        // may not reflect their actual position in the React tree or DOM. Without extreme
        // additional complexity (which was already attempted in a previous implementation and then
        // removed), it is not possible to know a focusable component's actual position relative to
        // existing focus siblings. TL;DR: If a specific index is required, provide a
        // `focusableIndex` prop on one or more children within a container.

        if (parent.lastDirectionOfTravel === -1) {
            // However, if the parent is a virtual container and the last interaction resulted in a
            // negative direction of travel, then insert the new node at the start

            parent.children.unshift(node);
        } else {
            parent.children.push(node);
        }

        TreeWorker.scheduleReorder(parent);
    }

    /**
     * Removes a focusable node and all its children from the tree and lookup table.
     *
     * @param id The ID of the node to be unregistered.
     */

    public static unregisterFocusable(lookupTable: ILookupTable, id: string, parentRemoved = false): void {
        const node = lookupTable[id];

        // If the node does not exist, it may have already been unregistered in reaction to its
        // parent being unmounted (`componentWillUnmount` is not neccessarily syncronous), in which
        // case we can safely abort here.

        if (!node) return;

        delete lookupTable[id];

        const {parent} = node;

        // If the parent does not exist, this is the root node. No further tree manipulation is
        // required, as the root node cannot be referenced as a child of any other node.

        if (!parent) return;

        // Nodes are spliced from the `children` array as they are unregistered (see below), so
        // shallow copy the array first to prevent index corruption during `forEach`.

        [...node.children].forEach(child => TreeWorker.unregisterFocusable(lookupTable, child.id, true));

        const indexToRemove = parent.children.indexOf(node);

        if (indexToRemove < 0) return;

        let hasRemoved = false;

        for (let i = 0; i < parent.children.length; i++) {
            if (i < indexToRemove) continue;

            if (i === indexToRemove && !hasRemoved) {
                parent.children.splice(indexToRemove, 1);

                hasRemoved = true;

                i--;

                continue;
            }

            const child = parent.children[i];

            child.index = i;
        }

        if (!parentRemoved) TreeWorker.scheduleReorder(parent);
    }

    /**
     * Updates the indices, order and bases of all nodes within a container, whenever a new node is
     * added, accounting for any custom focusable indices present.
     */

    public static reorderFocusableChildren(parent: FocusableNode, focusableChildren: FocusableNode[]): void {
        const orderedFocusableNodesWithIndices: Array<[FocusableNode, number, boolean]> = [];
        const reindexedChildren: FocusableNode[] = [];

        let hasIndexOverrides = false;
        let node: FocusableNode;
        let index: number;

        for (index = 0; (node = focusableChildren[index]); index++) {
            const hasIndexOverride = !isNaN(node.indexOverride);

            let sortingIndex: number;

            if (hasIndexOverride) {
                hasIndexOverrides = true;

                sortingIndex = node.indexOverride;
            } else {
                sortingIndex = index;
            }

            orderedFocusableNodesWithIndices.push([node, sortingIndex, hasIndexOverride]);
        }

        if (hasIndexOverrides) {
            // If any item in the array has an index override, re-sort it to ensure overrides are
            // taken into account in the final order

            orderedFocusableNodesWithIndices.sort(
                ([, aSortingIndex, aHasIndexOverride], [, bSortingIndex, bHasIndexOverride]) => {
                    if (aSortingIndex === bSortingIndex) {
                        if (process.env.NODE_ENV !== 'production') {
                            if (aHasIndexOverride && bHasIndexOverride) {
                                const componentDisplayName = ComponentDisplayName[parent.type];

                                console.warn(
                                    Errors.DUPLICATE_FOCUSABLE_INDICES(componentDisplayName, parent.id, aSortingIndex)
                                );
                            }
                        }

                        return aHasIndexOverride ? -1 : 1;
                    }

                    return aSortingIndex - bSortingIndex;
                }
            );
        }

        // Push the children in to the parent's `children` array in their final order

        for (index = 0; index < orderedFocusableNodesWithIndices.length; index++) {
            [node] = orderedFocusableNodesWithIndices[index];

            node.parent = parent;
            node.index = index;
            node.previousSibling = reindexedChildren[index - 1] ?? null;

            reindexedChildren.push(node);
        }

        parent.children = reindexedChildren;

        JustifiedFocusWorker.defineBases(parent);
    }

    /**
     * Moves pending child nodes without an associated parent, sitting in the temporary
     * `parentLookupTable`, into their parent node's `children` list, in the appropriate order, once
     * the parent node has mounted.
     *
     * @param parent The parent node to which children are moved to.
     */

    public static collectPendingChildren(parentLookupTable: IParentLookupTable, parent: FocusableNode): void {
        const pendingChildren = parentLookupTable[parent.id];

        TreeWorker.reorderFocusableChildren(parent, pendingChildren);

        delete parentLookupTable[parent.id];
    }

    /**
     * Schedules the reordering and rebasing of a parent's children in reaction to a node being
     * dynamically added or removed *after* the parent has mounted.
     *
     * Because multiple nodes may be added or removed in a single update, the purpose of this method
     * is to continually defer the reordering process until all child updates have been made, and
     * the list is stable. This prevents unnecessary iteration on each child update.
     */

    public static scheduleReorder(parent: FocusableNode): void {
        const timerId = TreeWorker.pendingReorderNodeIdMap[parent.id];

        if (timerId) clearTimeout(timerId);

        TreeWorker.pendingReorderNodeIdMap[parent.id] = platformSetTimeout(() => {
            TreeWorker.reorderFocusableChildren(parent, parent.children);

            delete TreeWorker.pendingReorderNodeIdMap[parent.id];
        });
    }

    private static insertNodeIntoParentLookupTable(
        parentLookupTable: IParentLookupTable,
        parentId: string,
        node: FocusableNode
    ): void {
        const children = parentLookupTable[parentId] ?? (parentLookupTable[parentId] = []);

        children.push(node);
    }
}

export default TreeWorker;
